import { useState } from 'react';
import { type PipelineNode, type SkillDef } from '@/mocks/pipeline';
import { PROMPT_PLACEHOLDER } from '@/pages/home/promptNode';

type RunLog = { at: string; message: string };

type Props = {
  selectedNode: PipelineNode | null;
  skills: SkillDef[];
  nodes: PipelineNode[];
  pipelineName: string;
  setPipelineName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  setSelectedId: (id: string) => void;
  onRunStep: (id: string) => void;
  onRemove: (id: string) => void;
  onUpdateNote: (id: string, note: string) => void;
  score: string;
  locked?: boolean;
  running?: boolean;
  runLogs?: RunLog[];
};

function scoreBarWidth(score: string): string {
  const n = parseFloat(score);
  if (!Number.isFinite(n)) return '0%';
  return `${Math.min(100, Math.max(0, (n / 12) * 100))}%`;
}

function scoreLabel(score: string): { num: string; den: string } {
  const slash = score.lastIndexOf('/');
  if (slash < 0) return { num: score, den: '12' };
  return { num: score.slice(0, slash), den: score.slice(slash + 1) };
}

export default function DetailsPanel({
  selectedNode, skills, nodes, pipelineName, setPipelineName, description, setDescription,
  setSelectedId, onRunStep, onRemove, onUpdateNote, score, locked = false, running = false,
  runLogs = [],
}: Props) {
  const [logsOpen, setLogsOpen] = useState(true);
  const skill = selectedNode ? skills.find((s) => s.id === selectedNode.skillId) : null;
  const displayLabel = scoreLabel(score);

  const runSection = (
    <div>
      <button onClick={() => setLogsOpen((o) => !o)} className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-texts hover:text-textp mb-2">
        <span>{running ? 'Run log' : 'Last run'}</span>
        <i className={`ri-arrow-down-s-line ${logsOpen ? '' : '-rotate-90'} transition-transform`} />
      </button>
      {logsOpen && (
        <ul className="space-y-1 max-h-48 overflow-y-auto font-mono text-[11px] text-texts">
          {runLogs.length === 0 ? (
            <li className="text-texts">Click Run to execute the pipeline.</li>
          ) : (
            runLogs.map((l, i) => (
              <li key={`${l.at}-${i}`} className="leading-snug">{l.message}</li>
            ))
          )}
        </ul>
      )}
    </div>
  );

  return (
    <div>
      {selectedNode ? (
        <div className="p-4 space-y-4">
          {runSection}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Skill</div>
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-textp">{skill?.name ?? selectedNode.skillId}</h2>
              {skill?.version ? (
                <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-surface border border-border text-texts">{skill.version}</span>
              ) : null}
            </div>
            {skill?.description ? (
              <p className="text-sm text-texts mt-1.5 leading-relaxed">{skill.description}</p>
            ) : null}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Notes</div>
            <textarea
              value={selectedNode.note ?? ''}
              onChange={(e) => onUpdateNote(selectedNode.id, e.target.value)}
              placeholder="Add notes for this step..."
              disabled={locked}
              className="w-full bg-surface/60 border border-border rounded-md px-2.5 py-2 text-sm text-textp placeholder:text-texts outline-none focus:border-accent resize-y min-h-[72px] disabled:opacity-60"
            />
          </div>

          <Schema title="Input schema" items={skill?.inputs ?? []} />
          <Schema title="Output schema" items={skill?.outputs ?? []} />

          {skill?.latency ? <div className="text-[11px] text-texts">{skill.latency}</div> : null}

          <div className="flex items-center gap-2 pt-2 border-t border-border">
            <button onClick={() => onRunStep(selectedNode.id)} disabled={locked} className="flex items-center gap-1.5 bg-accent hover:bg-accent-hover text-accent-text text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap disabled:opacity-60">
              <i className="ri-play-fill" /> Run this step
            </button>
            <button onClick={() => onRemove(selectedNode.id)} disabled={locked} className="text-error hover:bg-error/10 text-xs px-3 py-1.5 rounded-md whitespace-nowrap disabled:opacity-60">
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-5">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Name</div>
            <input
              value={pipelineName}
              onChange={(e) => setPipelineName(e.target.value)}
              disabled={locked}
              className="w-full bg-transparent text-lg font-semibold text-textp outline-none border-b border-transparent focus:border-accent disabled:opacity-60"
            />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Outcome</div>
            <textarea
              data-testid="outcome-details"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={PROMPT_PLACEHOLDER}
              disabled={locked}
              className="w-full bg-surface/60 border border-border rounded-md px-2.5 py-2 text-sm text-textp placeholder:text-texts outline-none focus:border-accent resize-y min-h-[72px] disabled:opacity-60"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-texts">Quality Score</span>
              <span className="text-sm font-mono text-textp">{displayLabel.num}<span className="text-texts">/{displayLabel.den}</span></span>
            </div>
            <div className="h-2 bg-surface rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-accent to-success" style={{ width: scoreBarWidth(score) }} />
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-texts mb-2">Steps</div>
            <ol className="space-y-1.5">
              {nodes.map((n, i) => {
                const s = skills.find((x) => x.id === n.skillId);
                return (
                  <li key={n.id}>
                    <button onClick={() => setSelectedId(n.id)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-surface text-left group">
                      <span className="w-5 h-5 rounded-md bg-surface border border-border text-[11px] font-mono text-texts flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="text-sm text-textp flex-1 truncate">{s?.name ?? n.skillId}</span>
                      <span className={`w-1.5 h-1.5 rounded-full ${n.status === 'success' ? 'bg-success' : n.status === 'warning' ? 'bg-warning' : n.status === 'error' ? 'bg-error' : n.status === 'running' ? 'bg-accent' : 'bg-texts'}`} />
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          {runSection}
        </div>
      )}
    </div>
  );
}

function Schema({ title, items }: { title: string; items: { name: string; type: string }[] }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">{title}</div>
      <div className="space-y-1">
        {items.map((it) => (
          <div key={it.name} className="flex items-center justify-between bg-surface/60 border border-border rounded-md px-2.5 py-1.5">
            <span className="font-mono text-[12px] text-textp">{it.name}</span>
            <span className="font-mono text-[11px] text-texts">{it.type}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
